import React from "react";
import { shotsReceived, points as pointsFor } from "../lib/scoring";

/**
 * The card, as a tournament board renders it: labels down the left, holes
 * across, OUT / IN / TOTAL tinted. Scrolls sideways on a phone with the
 * label column pinned — stacking the nines would break the OUT/IN reading
 * that lets someone check this against the paper card in their pocket.
 *
 * Read-only by default. Pass onChange to make the score row editable,
 * which is what the wide score-entry view uses.
 */

/** Tournament notation: red circle under par, black square over. */
export function markerClass(strokes, par) {
  if (strokes === undefined) return "mk-blank";
  if (strokes === null) return "mk-nr";
  const d = strokes - par;
  return d <= -2 ? "mk-eagle" : d === -1 ? "mk-birdie"
       : d === 0 ? "mk-par" : d === 1 ? "mk-bogey" : "mk-double";
}

export function Marker({ strokes, par }) {
  if (strokes === undefined) return <span className="mk mk-blank">·</span>;
  if (strokes === null) return <span className="mk mk-nr">NR</span>;
  return <span className={`mk ${markerClass(strokes, par)}`}>{strokes}</span>;
}

export function Legend() {
  return (
    <div className="legend">
      <span><i className="mk mk-eagle">2</i> Eagle or better</span>
      <span><i className="mk mk-birdie">3</i> Birdie</span>
      <span><i className="mk mk-bogey">5</i> Bogey</span>
      <span><i className="mk mk-double">6</i> Double or worse</span>
      <span style={{ color: "var(--red)" }}>• Shot received</span>
    </div>
  );
}

export default function Scorecard({ holes, scores, playingHandicap, onChange, showPoints = true }) {
  // the container's width, not the window's: this often sits inside a
  // leaderboard row, which is narrower than the page
  const box = React.useRef(null);
  const [wide, setWide] = React.useState(true);
  React.useEffect(() => {
    const el = box.current?.parentElement;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => setWide(e.contentRect.width >= 940));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const front = holes.slice(0, 9);
  const back = holes.slice(9);

  const sum = (hs, pick) => hs.reduce((a, h) => a + (pick(h) ?? 0), 0);
  const strokesOf = (h) => (scores[h.hole_number] == null ? 0 : scores[h.hole_number]);
  const ptsOf = (h) =>
    pointsFor(scores[h.hole_number] ?? null, h.par, shotsReceived(playingHandicap, h.stroke_index));

  const outGross = sum(front, strokesOf);
  const inGross = sum(back, strokesOf);
  const outPts = sum(front, ptsOf);
  const inPts = sum(back, ptsOf);

  const scoreCell = (h) => {
    const v = scores[h.hole_number];
    if (!onChange) return <td key={h.hole_number}><Marker strokes={v} par={h.par} /></td>;
    // The input itself carries the marker treatment, so a birdie is a red
    // disc and a bogey a black square while you're still typing — you can
    // see the shape of the round as it goes in.
    return (
      <td key={h.hole_number}>
        <input
          className={markerClass(v, h.par)}
          inputMode="numeric"
          aria-label={`Hole ${h.hole_number} score`}
          value={v === null ? "NR" : v ?? ""}
          onChange={(e) => {
            const raw = e.target.value.trim().toUpperCase();
            if (raw === "NR" || raw === "N") return onChange(h.hole_number, null);
            const d = raw.replace(/\D/g, "");
            onChange(h.hole_number, d === "" ? undefined : Math.min(20, Math.max(1, +d)));
          }} />
      </td>
    );
  };

  const shotCell = (h) => {
    const sh = shotsReceived(playingHandicap, h.stroke_index);
    return (
      <td key={h.hole_number} className={sh > 0 ? "shot" : "noshot"}>
        {sh > 0 ? "•".repeat(sh) : "–"}
      </td>
    );
  };

  /**
   * Two layouts, same data.
   *
   * Eighteen columns need about 960px, which the leaderboard's expanded
   * row never has — even on a laptop, because the panel sits inside a
   * table cell. So below that width the nines stack: nine columns each,
   * out above in, which fits comfortably and still reads as a card.
   */
  if (!wide) {
    return (
      <div className="scard-stack" ref={box}>
        {[[0, 9, "Out"], [9, 18, "In"]].map(([from, to, label]) => (
          <table className="scard scard-nine" key={label}>
            <tbody>
              <tr className="hole">
                <th className="lbl">Hole</th>
                {holes.slice(from, to).map((h) => <th key={h.hole_number}>{h.hole_number}</th>)}
                <th className="tot">{label}</th>
              </tr>
              <tr>
                <td className="lbl">Par</td>
                {holes.slice(from, to).map((h) => <td className="par" key={h.hole_number}>{h.par}</td>)}
                <td className="tot">{sum(holes.slice(from, to), (h) => h.par)}</td>
              </tr>
              <tr>
                <td className="lbl">Index</td>
                {holes.slice(from, to).map((h) => (
                  <td className="muted" key={h.hole_number}>{h.stroke_index}</td>))}
                <td className="tot">—</td>
              </tr>
              <tr>
                <td className="lbl">Shots</td>
                {holes.slice(from, to).map(shotCell)}
                <td className="tot">—</td>
              </tr>
              <tr>
                <td className="lbl" style={{ color: "var(--text)" }}>Score</td>
                {holes.slice(from, to).map(scoreCell)}
                <td className="tot">
                  {holes.slice(from, to).reduce((a, h) => a + strokesOf(h), 0) || "–"}
                </td>
              </tr>
              {showPoints && (
                <tr>
                  <td className="lbl">Points</td>
                  {holes.slice(from, to).map((h) => (
                    <td className="muted" key={h.hole_number}>{ptsOf(h)}</td>))}
                  <td className="tot">{sum(holes.slice(from, to), ptsOf)}</td>
                </tr>
              )}
            </tbody>
          </table>
        ))}
      </div>
    );
  }

  return (
    <div className="cardscroll" ref={box}>
      <table className="scard">
        <tbody>
          <tr className="hole">
            <th className="lbl">Hole</th>
            {front.map((h) => <th key={h.hole_number}>{h.hole_number}</th>)}
            <th className="tot">Out</th>
            {back.map((h) => <th key={h.hole_number}>{h.hole_number}</th>)}
            <th className="tot">In</th>
            <th className="tot">Total</th>
          </tr>

          <tr>
            <td className="lbl">Par</td>
            {front.map((h) => <td className="par" key={h.hole_number}>{h.par}</td>)}
            <td className="tot">{sum(front, (h) => h.par)}</td>
            {back.map((h) => <td className="par" key={h.hole_number}>{h.par}</td>)}
            <td className="tot">{sum(back, (h) => h.par)}</td>
            <td className="tot">{sum(holes, (h) => h.par)}</td>
          </tr>

          <tr>
            <td className="lbl">Stroke index</td>
            {front.map((h) => <td className="muted" key={h.hole_number}>{h.stroke_index}</td>)}
            <td className="tot">—</td>
            {back.map((h) => <td className="muted" key={h.hole_number}>{h.stroke_index}</td>)}
            <td className="tot">—</td>
            <td className="tot">—</td>
          </tr>

          <tr>
            <td className="lbl">Shots</td>
            {front.map(shotCell)}
            <td className="tot">—</td>
            {back.map(shotCell)}
            <td className="tot">—</td>
            <td className="tot">{playingHandicap}</td>
          </tr>

          <tr>
            <td className="lbl" style={{ color: "var(--text)" }}>Score</td>
            {front.map(scoreCell)}
            <td className="tot">{outGross || "–"}</td>
            {back.map(scoreCell)}
            <td className="tot">{inGross || "–"}</td>
            <td className="tot">{outGross + inGross || "–"}</td>
          </tr>

          {showPoints && (
            <tr>
              <td className="lbl">Points</td>
              {front.map((h) => <td className="muted" key={h.hole_number}>{ptsOf(h)}</td>)}
              <td className="tot">{outPts}</td>
              {back.map((h) => <td className="muted" key={h.hole_number}>{ptsOf(h)}</td>)}
              <td className="tot">{inPts}</td>
              <td className="tot">{outPts + inPts}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
